import type { VendorTagType } from './tagTypes';
import { tagDetection } from './tagTypes';

export const is1x1Pixel = (tag: string): boolean => {
 const lowerTag = tag.toLowerCase();
 return (lowerTag.includes('height="1"') || lowerTag.includes('height=1')) && 
        (lowerTag.includes('width="1"') || lowerTag.includes('width=1'));
};

export const macroReplacements = {
 dcm_ins: (tag: string) => {
   const openTagMatch = tag.match(/<ins[^>]*>/);
   if (!openTagMatch) return tag;

   // Split the tag into opening tag and script portion
   const tagStart = openTagMatch[0];
   const tagEnd = tag.substring(tag.indexOf("<script"));
   const is1x1 = is1x1Pixel(tagStart);

   // Parse existing attributes while preserving formatting
   let attributes = tagStart
     .slice(4, -1) // Remove '<ins' and '>'
     .trim()
     .split(/\s+(?=data-|class|style)/);

   // Check for existing attributes
   const hasKeywords = attributes.some(attr => attr.includes('data-dcm-keywords'));
   const hasClickTracker = attributes.some(attr => attr.includes('data-dcm-click-tracker'));
   const hasLandingPage = attributes.some(attr => attr.includes('data-dcm-landing-page-escapes'));

   // Update existing attributes
   attributes = attributes.map(attr => {
     if (attr.includes('data-dcm-https-only')) {
       return "data-dcm-https-only=''";
     }
     if (attr.includes('data-dcm-app-id')) {
       return "data-dcm-app-id='%{APP_BUNDLE}'";
     }
     // Handle empty click tracker - replace it with proper macro
     if (attr.includes('data-dcm-click-tracker') && !is1x1) {
       return "data-dcm-click-tracker='%{tURL0}'";
     }
     return attr;
   });

   // Add new attributes only if they don't exist
   const newAttributes = [];
   
   if (!is1x1 && !hasClickTracker) {
     newAttributes.push("data-dcm-click-tracker='%{tURL0}'");
   }
   if (!hasLandingPage) {
     newAttributes.push("data-dcm-landing-page-escapes='0'");
   }
   if (!hasKeywords) {
     newAttributes.push("data-dcm-keywords='im_token=%{TOKEN}'");
   }

   // Reconstruct the tag with proper formatting
   const allAttributes = [...attributes, ...newAttributes];
   const formattedAttributes = allAttributes
     .filter(Boolean)
     .join('\n    ');

   return `<ins ${formattedAttributes}>\n  ${tagEnd}`;
 },

 dcm_javascript_tag: (tag: string): string => {
   let modifiedTag = tag;
   const is1x1 = is1x1Pixel(tag);
   
   if (tag.includes('googletag.cmd.push')) {
     modifiedTag = modifiedTag
       .replace(/\[timestamp\]/g, '%{TIMESTAMP}')
       .replace(/ord=([^;]+)/, 'ord=%{TIMESTAMP}');

     if (!is1x1) {
       modifiedTag = modifiedTag
         .replace(/click0=([^;]+)/, 'click0=%{etURL0}')
         .replace(/(var dcm = {[^}]*})/, '$1\ndcm.click0=\'%{etURL0}\';');
     }
   }
   
   if (tag.includes('var dcm')) {
     modifiedTag = modifiedTag
       .replace(/\[timestamp\]/g, '%{TIMESTAMP}')
       .replace(/ord:\s*['"]\d*['"]/, 'ord: \'%{TIMESTAMP}\'');

     if (!is1x1) {
       modifiedTag = modifiedTag
         .replace(/click0:\s*['"][^'"]*['"]/, 'click0: \'%{etURL0}\'')
         .replace(/(var dcm\s*=\s*{[^}]*})/, '$1\ndcm.click0=\'%{etURL0}\';');
     }
   }

   return modifiedTag;
 },

 dcm_standard: (tag: string): string => {
   const is1x1 = is1x1Pixel(tag);
   let modifiedTag = tag
     .replace(/\[timestamp\]/g, '%{TIMESTAMP}')
     .replace(/ord=[^&\s"]*/, 'ord=%{TIMESTAMP}')
     .replace(/BORDER=0/, 'BORDER="0"')
     .replace(/WIDTH=(\d+) HEIGHT=(\d+)/, (match, width, height) => 
       `WIDTH="${width}" HEIGHT="${height}"`);

   if (!is1x1) {
     modifiedTag = modifiedTag.replace(/HREF=".*?"/, 'HREF="%{URL0}"');
   }

   return modifiedTag;
 },

 dcm_javascript: (tag: string): string => {
   const is1x1 = is1x1Pixel(tag);
   let modifiedTag = tag.replace(/\[timestamp\]/g, '%{TIMESTAMP}');

   if (!is1x1) {
     modifiedTag = modifiedTag.replace(/ord=/, 'click0=%{etURL0};ord=');
   }

   return modifiedTag;
 },

 sizmek_javascript: (tag: string): string => {
   const is1x1 = is1x1Pixel(tag);
   let modifiedTag = tag
     .replace(/\[timestamp\]/g, '%{TIMESTAMP}')
     .replace(/%time%/g, '%{TIMESTAMP}');

   if (!is1x1) {
     modifiedTag = modifiedTag
       .replace(/href=".*?"/, 'href="%{URL0}"')
       .replace(/"><\/script>/, '&ncu=$$%{pURL0}$$"></script>');
   }

   modifiedTag = modifiedTag
     .replace(/" border=/, '&ord=%{TIMESTAMP}&mb=1" border=')
     .replace(/" target=/, '&ord=%{TIMESTAMP}&mb=1" target=');

   return modifiedTag;
 },

 sizmek_standard: (tag: string): string => tag
     .replace(/\[timestamp\]/g, '%{TIMESTAMP}&mb=1')
     .replace(/%time%/g, '%{TIMESTAMP}&mb=1'),

 flashtalking: (tag: string): string => {
   const is1x1 = is1x1Pixel(tag);
   let modifiedTag = tag;

   if (!is1x1) {
     modifiedTag = modifiedTag.replace(/ftClick = ""/, 'ftClick = "%{tURL0}"');
   }

   return modifiedTag
     .replace(/ftLat = ""/, 'ftLat = "%{LAT}"')
     .replace(/ftLong = ""/, 'ftLong = "%{LONG}"')
     .replace(/ftCustom = ""/, 'ftCustom = "%{TOKEN}"');
 },

 abc: (tag: string): string => tag
      .replace(/\[PID\]\s*/g, '%{INSERTION_ORDER}')
      .replace(/\[CRID\]\s*/g, '%{CREATIVE}')
      .replace(/\[CACHEBUSTER\]\s*/g, '%{TIMESTAMP}')
};

export const detectTagType = (tag: string): VendorTagType | null => {
 if (!tag) return null;
 
 if (tagDetection.isDCMIns(tag)) {
   return 'dcm_ins';
 }

 if (tagDetection.isDCMJavaScript(tag)) {
   return 'dcm_javascript_tag';
 }
 
 if (tagDetection.isDoubleClick(tag)) {
   return tag.includes('script') ? 'dcm_javascript' : 'dcm_standard';
 }
 
 if (tagDetection.isSizmekJavascript(tag)) {
   return 'sizmek_javascript';
 }
 
 if (tagDetection.isSizmekStandard(tag)) {
   return 'sizmek_standard';
 }
 
 if (tagDetection.isFlashTalking(tag)) {
   return 'flashtalking';
 }

 if (tagDetection.isABC(tag)) {
   return 'abc';
 }

 return null;
};

export const addMacrosToTag = (tag: string, tagType: VendorTagType): string => 
 macroReplacements[tagType]?.(tag) ?? tag;

// Matches backend get_tag_with_click_tag logic
export const wrapWithClickTag = (
  autoTag: string,
  imageUrl: string,
  size: string
): string => {
  if (!autoTag || !imageUrl) {
    return autoTag;
  }

  if (size && size !== 'Autodetect') {
    const splitSize = size.split('x');
    if (splitSize.length === 2) {
      const width = splitSize[0];
      const height = splitSize[1];
      return `<a href="%{URL0}" target="_blank" height="${height}" width="${width}"><img src="${imageUrl}" border="0"/></a> ${autoTag}`;
    }
  }

  return `<a href="%{URL0}" target="_blank"><img src="${imageUrl}" border="0"/></a> ${autoTag}`;
};